import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { FabricCanvas } from '../../types/fabric';
import { SearchableSelect } from '../SearchableSelect';
import { Skeleton } from '../Skeleton';
import { useToast } from '../Notifications';
import { BackgroundColorTool } from './BackgroundColorTool';
import { importCatalogAssetAsUpload, type StagedImage } from './StagedImage';
import { Spinner } from '../Spinner';
import { ToolSection } from './shared/ToolSection';

interface ArtworkToolProps {
  canvas: FabricCanvas | null;
}

export function ArtworkTool({ canvas }: ArtworkToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [selectedCat, setSelectedCat] = useState<string>('');
  const [page, setPage] = useState(1);
  // Accumulated across pages — "Load more" must append, not replace, the grid.
  const [allArtworks, setAllArtworks] = useState<any[]>([]);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  // Same staged-then-wizard flow as UploadTool.tsx, per direct user request
  // that picking an artwork go through the same vectorize/remove-background/
  // select-colors/crop steps a direct file upload does, instead of dropping
  // straight onto the canvas.
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);

  const { data: catData } = useQuery({
    queryKey: ['artwork-categories'],
    queryFn:  () => fetch('/api/artworks/categories').then(r => r.json()),
    staleTime: 300_000,
  });

  const { data: artData, isFetching: artworksLoading } = useQuery({
    queryKey: ['artworks', selectedCat, page],
    queryFn:  () =>
      fetch(`/api/artworks?${selectedCat ? `categoryId=${selectedCat}&` : ''}page=${page}`).then(r => r.json()),
    staleTime: 60_000,
  });

  useEffect(() => { setPage(1); setAllArtworks([]); setLoadedIds(new Set()); }, [selectedCat]);

  useEffect(() => {
    if (!artData?.artworks) return;
    setAllArtworks(prev => page === 1 ? artData.artworks : [...prev, ...artData.artworks]);
  }, [artData, page]);

  const markLoaded = (id: string) => setLoadedIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));

  const selectArtwork = async (a: any) => {
    setImportingId(a.id);
    try {
      const result = await importCatalogAssetAsUpload(a.image_path);
      if (!result) {
        showToast(t('artwork.importFailed'), 'error');
        return;
      }
      setStaged(result);
    } finally {
      setImportingId(null);
    }
  };

  // The categories endpoint returns { tree: [...] } — not { categories: [...] }.
  const categories: any[] = catData?.tree ?? [];
  const categoryOptions = categories.map(c => ({ value: String(c.id), label: c.title ?? c.name }));

  if (staged) {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{t('artwork.editingArtwork')}</span>
            <button onClick={() => setStaged(null)} title={t('upload.cancelUpload')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>
              ×
            </button>
          </div>
          {/* key={staged.uploadId} forces a fresh wizard instance per artwork —
              same reasoning as UploadTool.tsx's identical key. */}
          <BackgroundColorTool key={staged.uploadId} canvas={canvas} staged={staged} onDone={() => setStaged(null)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('artwork.sectionCategory')} divider={false}>
        <SearchableSelect
          options={categoryOptions}
          value={selectedCat}
          onChange={setSelectedCat}
          placeholder={t('artwork.selectCategory')}
          searchPlaceholder={t('artwork.searchCategory')}
          noMatchesLabel={t('artwork.noMatches')}
        />
      </ToolSection>

      <ToolSection title={t('artwork.sectionBrowse')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {allArtworks.map((a: any) => (
            <button
              key={a.id}
              onClick={() => selectArtwork(a)}
              disabled={importingId !== null}
              style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 4, background: 'none', cursor: importingId !== null ? 'default' : 'pointer', padding: 4, aspectRatio: '1', overflow: 'hidden' }}
              title={a.label ?? a.name ?? ''}
            >
              {!loadedIds.has(a.id) && <Skeleton style={{ position: 'absolute', inset: 4 }} />}
              {/* `thumb_url` is a small pre-generated WebP — used here for grid
                  speed; the wizard's staged preview (above) uses the full
                  `image_path`. */}
              <img
                src={a.thumb_url ?? a.image_path}
                alt={a.label ?? a.name ?? ''}
                style={{ width: '100%', height: '100%', objectFit: 'contain', visibility: loadedIds.has(a.id) ? 'visible' : 'hidden' }}
                loading="lazy"
                onLoad={() => markLoaded(a.id)}
                onError={() => markLoaded(a.id)}
              />
              {importingId === a.id && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)' }}>
                  <Spinner size={18} />
                </div>
              )}
            </button>
          ))}
          {artworksLoading && page === 1 && allArtworks.length === 0 &&
            Array.from({ length: 9 }).map((_, i) => (
              <div key={`skeleton-${i}`} style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', aspectRatio: '1', padding: 4, boxSizing: 'border-box' }}>
                <Skeleton />
              </div>
            ))
          }
        </div>

        {allArtworks.length === 0 && !artworksLoading && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
            {selectedCat ? t('artwork.empty') : t('artwork.selectFirst')}
          </p>
        )}

        {artData?.loadMoreFlag === 1 && (
          <button onClick={() => setPage(p => p + 1)} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }} disabled={artworksLoading}>
            {artworksLoading ? t('artwork.loading') : t('artwork.loadMore')}
          </button>
        )}
      </ToolSection>
    </div>
  );
}
